/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import debounce from 'lodash.debounce';
import { observer } from 'mobx-react-lite';
import Image from 'next/image';
import Link from 'next/link';
import { transparentize } from 'polished';
import { Search } from '@styled-icons/boxicons-regular/Search';
import { useTranslations } from 'next-intl';

import { flagForCountryCode, getFallbackImage } from '@/utils';
import Loader from '@/components/loader';

const Container = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  position: relative;
  width: calc(100% - 2.75em);
`;

const SearchIcon = styled(Search)`
  left: 1em;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(calc(-50% + 1px));
`;

const Input = styled.input.attrs({ type: 'search' })`
  appearance: none;
  background: none;
  border: none;
  box-sizing: border-box;
  font-family: ${({ theme }) => theme.fonts.primary};
  color: ${({ theme }) => theme.colors.typography.primary};
  caret-color: ${({ theme }) => theme.colors.typography.primary};
  display: block;
  flex-grow: 1;
  font-size: 1.1em;
  height: 100%;
  margin: 0 .5em 0 0;
  outline: none;
  padding: .65em 1em .65em 2.75em;
  transition: all 250ms;

  &:focus, &:hover {
    outline: none;
  }
`;

const Suggestions = styled.ul`
  border-radius: 0.5em;
  list-style: none;
  margin: 0;
  max-height: 20em;
  overflow-y: auto;
  padding: 0;
  position: absolute;
  top: 100%;
  width: 100%;

  ${({ theme }) => css`
    background-color: ${theme.colors.background.card};
    box-shadow: ${theme.shadows.primary};

    a {
      color: ${theme.colors.typography.primary};
      display: block;

      + a {
        border-top: 1px solid rgba(0, 0, 0, .05);
      }

      &:hover {
        background-color: ${transparentize(.95, theme.colors.primary)};
      }
    }
  `};
`;

const Suggestion = styled.li`
  align-items: center;
  cursor: pointer;
  display: flex;
  padding: 1em 1.25em;

  .loader {
    margin-right: 1em;
  }
`;

const SuggestionName = styled.span`
  flex-grow: 1;
  margin-left: 1em;
  text-align: left;
`;

const SearchField = ({
  className = '',
  isLoadingSuggestions,
  onBlur,
  onChange = () => {},
  onEnter,
  onFocus,
  onSuggestionClick,
  search,
  searchTerm,
  setSearchTerm,
  suggestions
}) => {
  const debounced = debounce(search, 500);
  const doSearch = useCallback(debounced, [debounced]);
  const debouncedChange = debounce(onChange, 500);
  const doDebouncedChange = useCallback(debouncedChange, []);
  const showSuggestions = isLoadingSuggestions || (Array.isArray(suggestions) && suggestions.length > 0);
  const theme = useTheme();
  const t = useTranslations('searchField');
  const [isFocused, setIsFocused] = useState(false);

  const handleSearch = useCallback(e => {
    setSearchTerm(e);

    if (e.target.value.trim().length >= 3 || !e.target.value.length) {
      doSearch(e);
      doDebouncedChange(e);
    }
  }, [doSearch, setSearchTerm, doDebouncedChange]);

  const handleKeyUp = e => {
    if (e.key === 'Enter' && e.target.value.trim().length >= 3) {
      onEnter && onEnter();
    }
  };

  const handleClick = suggestion => () => {
    if (typeof onSuggestionClick === 'function') {
      onSuggestionClick(suggestion);
    }
  };

  const handleBlur = () => {
    if (typeof onBlur) onBlur();
    setIsFocused(false);
  };

  const handleFocus = () => {
    if (typeof onFocus) onFocus();
    setIsFocused(true);
  };

  return (
    <Container className={`search-field ${className} ${isFocused ? 'search-field--focused' : 'search-field--blurred'}`}>
      <SearchIcon color={theme.colors.typography.subtle} size={24} />
      <Input
        onBlur={handleBlur}
        onChange={handleSearch}
        onFocus={handleFocus}
        onKeyUp={handleKeyUp}
        placeholder={t('placeholder')}
        value={searchTerm} />
      {showSuggestions &&
        <Suggestions>
          {isLoadingSuggestions &&
            <Suggestion><Loader mode="inline" size={16} /> {t('loading')}</Suggestion>
          }
          {suggestions.map(suggestion => (
            (<Link
              key={suggestion.id}
              passHref
              href={`/brands/${suggestion.country.slug}/${suggestion.slug}`}
              onClick={handleClick(suggestion)}>

              <Suggestion>
                <Image
                  alt={suggestion.name}
                  fallback={getFallbackImage(suggestion.type)}
                  height={32}
                  src={suggestion.imageUrl}
                  unoptimized
                  width={48}
                  style={{
                    maxWidth: "100%",
                    height: "auto"
                  }} />
                <SuggestionName>{suggestion.name}</SuggestionName>
                {flagForCountryCode(suggestion.countryId)}
              </Suggestion>

            </Link>)
          ))}
        </Suggestions>
      }
    </Container>
  );
};

export default observer(SearchField);
